import React from 'react';
import { FaChartBar, FaDatabase, FaCloud, FaCogs, FaRobot, FaServer } from 'react-icons/fa';
import { AiOutlineCluster } from 'react-icons/ai';
import { BiNetworkChart } from 'react-icons/bi';

export const services = [
  {
    icon: <FaChartBar size={20} />,
    title: "Advanced & Actionable insights. Reporting and Dashboarding",
    path: "/services/reporting-dashboarding"
  },
  {
    icon: <FaServer size={20} />,
    title: "Data Engineering (ELT/ETL)",
    path: "/services/data-engineering"
  },
  {
    icon: <AiOutlineCluster size={20} />,
    title: "Data Science and Machine Learning",
    path: "/services/data-science"
  },
  {
    icon: <BiNetworkChart size={20} />,
    title: "System to System data Integration",
    path: "/services/system-integration"
  },
  {
    icon: <FaCogs size={20} />,
    title: "Data Lakehouse and Warehouse management",
    path: "/services/data-lakehouse"
  },
  {
    icon: <FaRobot size={20} />,
    title: "Enterprise data modeling (Star/Snowflake schema)",
    path: "/services/data-modeling"
  },
  {
    icon: <FaCloud size={20} />,
    title: "Supported cloud data platforms (AWS/Azure/Google)",
    path: "/services/cloud-platforms"
  },
  {
    icon: <FaDatabase size={20} />,
    title: "Oracle/Postgres/mySQL development and performance optimization",
    path: "/services/database-development"
  }
];

export const detailedServices = [
  {
    title: "Advanced & Actionable Insights: Reporting & Dashboarding Service Offering",
    description: `Providing businesses with <strong>advanced, data-driven insights</strong> to enhance decision-making. By leveraging cutting-edge analytics tools, we transform raw data into <strong>actionable intelligence</strong> through <strong>customized reports and interactive dashboards</strong>.`,
    features: [
      "<strong>Custom Dashboards</strong> – Tailored visualizations for real-time monitoring of KPIs",
      "<strong>Automated Reporting</strong> – Scheduled and dynamic reports to ensure timely data access",
      "<strong>Predictive Analytics</strong> – AI-driven forecasts for proactive decision-making",
      "<strong>Data Integration</strong> – Seamless connectivity with multiple data sources",
      "<strong>User-Centric Design</strong> – Intuitive and interactive dashboards for all business users"
    ],
    impact: [
      "<strong>Improved Decision-Making</strong> – Gain real-time visibility into business performance",
      "<strong>Operational Efficiency</strong> – Reduce manual reporting efforts with automation",
      "<strong>Enhanced Strategy</strong> – Uncover patterns and trends to drive business growth"
    ]
  },
  {
    title: "Data Engineering (ETL/ELT) as a Service",
    description: "Helping organizations efficiently collect, transform, and manage data for seamless analytics and business intelligence. We design and implement <strong>scalable, high-performance data pipelines</strong> to ensure clean, structured, and readily available data for decision-making.",
    features: [
      "<strong>ETL & ELT Pipelines</strong> – Extract, transform, and load data using the best-fit approach",
      "<strong>Cloud & On-Prem Integration</strong> – Seamless data movement across cloud platforms and on-premises systems",
      "<strong>Real-Time & Batch Processing</strong> – Support for both real-time streaming and scheduled batch jobs",
      "<strong>Data Quality & Governance</strong> – Ensure accuracy, consistency, and compliance with data policies",
      "<strong>Scalability & Performance Optimization</strong> – Efficient handling of large volumes of structured and unstructured data"
    ],
    impact: [
      "<strong>Reliable Data for Analytics</strong> – Ensure high-quality, trusted data for insights",
      "<strong>Optimized Data Workflows</strong> – Automate data ingestion and transformation to improve efficiency",
      "<strong>Cost-Effective Data Management</strong> – Reduce infrastructure costs with scalable and optimized pipelines"
    ]
  },
  {
    title: "Data Science & Machine Learning as a Service",
    description: "Empowering businesses with advanced analytics, AI-driven insights, and predictive modeling to drive smarter decision-making. By leveraging cutting-edge algorithms and scalable AI solutions, we transform raw data into actionable intelligence.",
    features: [
      "<strong>Predictive Analytics</strong> – Forecast trends, customer behavior, and business outcomes",
      "<strong>AI-Powered Insights</strong> – Leverage machine learning models to uncover hidden patterns in data",
      "<strong>Natural Language Processing (NLP)</strong> – Enhance automation with AI-driven text, speech, and sentiment analysis",
      "<strong>Computer Vision</strong> – Analyze images and videos for intelligent automation",
      "<strong>Custom ML Model Development</strong> – Tailor AI solutions to meet specific business needs",
      "<strong>MLOps & Model Deployment</strong> – Deploy scalable ML solutions with continuous monitoring and optimization"
    ],
    impact: [
      "<strong>Data-Driven Decision Making</strong> – Improve strategy with AI-backed insights",
      "<strong>Operational Efficiency</strong> – Automate repetitive tasks and enhance business processes",
      "<strong>Competitive Advantage</strong> – Stay ahead with intelligent automation and real-time analytics"
    ]
  },
  {
    title: "System-to-System Data Integration as a Service",
    description: "Enabling seamless, automated data exchange between disparate applications, databases, and cloud platforms. We design <strong>scalable, secure, and high-performance integration solutions</strong> to ensure <strong>real-time data synchronization</strong> and operational efficiency.",
    features: [
      "<strong>API-Based & Middleware Integration</strong> – Connect systems via REST, SOAP, or event-driven architectures",
      "<strong>Cloud & On-Premises Connectivity</strong> – Enable data flow across cloud platforms (AWS, Azure, GCP) and legacy systems",
      "<strong>Real-Time & Batch Processing</strong> – Support for instant data synchronization or scheduled batch updates",
      "<strong>Data Transformation & Mapping</strong> – Standardize and enrich data for consistency across systems",
      "<strong>Secure & Compliant Transfers</strong> – Ensure encryption, authentication, and compliance with industry standards"
    ],
    impact: [
      "<strong>Eliminate Data Silos</strong> – Enable unified data access across business applications",
      "<strong>Improve Operational Efficiency</strong> – Reduce manual data entry and errors with automation",
      "<strong>Enhance Business Agility</strong> – Accelerate digital transformation with seamless integrations"
    ]
  },
  {
    title: "Data Lakehouse & Warehouse Management as a Service",
    description: "Providing businesses with a robust, scalable, and efficient solution for storing, managing, and analyzing vast amounts of data. By combining the flexibility of a <strong>data lake</strong> with the structured efficiency of a <strong>data warehouse</strong>, we enable seamless data access, governance, and analytics.",
    features: [
      "<strong>Data Lakehouse Architecture</strong> – Unified storage for structured and unstructured data with optimized query performance",
      "<strong>Data Warehousing Solutions</strong> – Scalable, high-performance data storage for BI and reporting",
      "<strong>Real-Time & Batch Processing</strong> – Support for both streaming and scheduled data workloads",
      "<strong>ETL/ELT Data Pipelines</strong> – Automated data ingestion, transformation, and integration",
      "<strong>Data Governance & Security</strong> – Implement access controls, encryption, and compliance measures",
      "<strong>Cloud & Hybrid Deployment</strong> – Support for AWS, Azure, GCP, and on-premise solutions"
    ],
    impact: [
      "<strong>Faster Insights & Analytics</strong> – Enable real-time and historical data analysis for informed decision-making",
      "<strong>Optimized Storage & Performance</strong> – Reduce costs with efficient data management strategies",
      "<strong>Improved Data Governance</strong> – Ensure compliance and data quality across the organization"
    ]
  },
  {
    title: "Enterprise Data Modeling as a Service",
    description: "Helping organizations design, structure, and optimize their data architecture for efficient storage, integration, and analytics. By creating scalable, well-governed data models, we ensure consistency, accuracy, and accessibility across enterprise systems.",
    features: [
      "<strong>Conceptual, Logical & Physical Data Models</strong> – Design comprehensive data blueprints tailored to business needs",
      "<strong>Data Standardization & Governance</strong> – Establish data consistency, security, and compliance frameworks",
      "<strong>Relational & NoSQL Database Modeling</strong> – Optimize data structures for performance and scalability",
      "<strong>Metadata Management</strong> – Enhance data discoverability and lineage tracking",
      "<strong>Scalable & Future-Proof Architecture</strong> – Ensure adaptability to evolving business and technology landscapes",
      "<strong>Cloud & Hybrid Integration</strong> – Seamless compatibility with cloud platforms and on-prem environments"
    ],
    impact: [
      "<strong>Optimized Data Management</strong> – Improve data accuracy and accessibility across the organization",
      "<strong>Enhanced Decision-Making</strong> – Enable reliable, high-quality data for analytics and reporting",
      "<strong>Increased Operational Efficiency</strong> – Reduce redundancy and improve system performance"
    ]
  },
  {
    title: "Supported Cloud Data Platforms as a Service",
    description: "Enabling businesses to leverage the full potential of leading cloud-based data ecosystems for storage, processing, and analytics. We provide expert guidance, deployment, and optimization for <strong>modern, scalable, and secure cloud data solutions</strong>.",
    features: [
      "<strong>Multi-Cloud & Hybrid Support</strong> – Expertise in AWS, Azure, Google Cloud, and hybrid environments",
      "<strong>Data Warehousing & Lakehouse</strong> – Implementation of cloud-based solutions like Snowflake, BigQuery, Redshift, and Databricks",
      "<strong>Serverless & Scalable Data Processing</strong> – Utilize cloud-native tools like AWS Glue, Azure Synapse, and Google Dataflow",
      "<strong>ETL/ELT Pipelines & Integration</strong> – Seamless data movement and transformation across cloud platforms",
      "<strong>Security & Compliance</strong> – Ensure encryption, role-based access control, and regulatory compliance",
      "<strong>Performance Optimization</strong> – Cost-effective scaling, storage management, and query performance tuning"
    ],
    impact: [
      "<strong>Reduced Infrastructure Costs</strong> – Leverage cloud scalability and pay-as-you-go pricing",
      "<strong>Accelerated Data Insights</strong> – Real-time and high-performance analytics",
      "<strong>Future-Proof Data Strategy</strong> – Adopt flexible, cloud-native architectures for long-term growth"
    ]
  },
  {
    title: "Database Development & Performance Optimization as a Service",
    description: "Ensuring that businesses have highly efficient, scalable, and reliable databases to support their mission-critical applications and analytics. We design, develop, and fine-tune databases to <strong>maximize performance, reduce costs, and enhance data accessibility</strong>.",
    features: [
      "<strong>Custom Database Design & Development</strong> – Tailored relational (SQL) and NoSQL database solutions",
      "<strong>Query Optimization & Indexing</strong> – Fine-tune SQL queries and indexing strategies for faster performance",
      "<strong>Scalability & High Availability</strong> – Implement replication, sharding, and load balancing for optimal data access",
      "<strong>ETL & Data Processing Optimization</strong> – Streamline data ingestion and transformation workflows",
      "<strong>Database Security & Compliance</strong> – Encryption, access control, and compliance with industry standards",
      "<strong>Cloud & On-Prem Database Support</strong> – Expertise in MySQL, PostgreSQL, SQL Server, Oracle, MongoDB, and cloud-native databases"
    ],
    impact: [
      "<strong>Faster Query Performance</strong> – Improve response times for analytics and applications",
      "<strong>Optimized Resource Utilization</strong> – Reduce infrastructure costs with efficient database tuning",
      "<strong>Reliable & Secure Data Access</strong> – Ensure uninterrupted operations with robust security and backup strategies"
    ]
  }
]; 